import { useState, useEffect, useRef } from 'react';
import { MagnifyingGlassIcon, ArrowLeftIcon, CheckIcon } from '@phosphor-icons/react';
import NotebookCard from '../shared/notebook-card';
import axios from 'axios';
import { ContextMenu } from '../../components/contextmenu/ContextMenu';
import MyNotebooksLoader from '../../components/loaders/MyNotebookLoader';
import { EditModal } from '../../components/modals/EditModal/EditModal';
import { DeleteModal } from '../../components/modals/DeleteModal/DeleteModal';
import { PencilSimpleIcon, TrashIcon } from '@phosphor-icons/react';
import { updateNotebook, archiveNotebook } from '../../services/notebooks';

const getNotebooks = async (packId) => {
  try {
    const response = await axios.get(`/api/v2/notebooks/${packId}`);

    return response.data.data || response.data;
  } catch (error) {
    throw error;
  }
};

const normalizeNotebook = (notebook) => {
  return {
    id: notebook._id || notebook.id,
    _id: notebook._id || notebook.id,
    name: notebook.title || notebook.name,
    title: notebook.title || notebook.name,
    questionCount: notebook.notebookQuestions ? notebook.notebookQuestions.length : notebook.questionCount || 0,
    createdAt: notebook.createdAt,
    updatedAt: notebook.updatedAt,
    packId: notebook.packId,
    isArchived: notebook.isArchived,
    isPinned: notebook.isPinned,
    userId: notebook.userId,
    type: 'notebook',
  };
};

export const MyNotebooks = ({ onNotebookClick, onScreenChange, onBack, selectedPack }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('Qs (High to Low)');
  const [activeFilter, setActiveFilter] = useState('All Notebooks');
  const [notebooks, setNotebooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [newNotebookName, setNewNotebookName] = useState('');
  const [createLoading, setCreateLoading] = useState(false);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const containerRef = useRef(null);
  const [contextMenu, setContextMenu] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [notebookName, setNotebookName] = useState('');
  const [currentNotebook, setCurrentNotebook] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const myRef = useRef(null);

  const user = JSON.parse(localStorage.getItem('userProfile'));

  useEffect(() => {
    const checkIfTablet = () => {
      setIsTablet(window.innerWidth > 768 && window.innerWidth <= 1024);
    };
    checkIfTablet();
    window.addEventListener('resize', checkIfTablet);
    return () => window.removeEventListener('resize', checkIfTablet);
  }, []);

  const sortOptions = ['Qs (High to Low)', 'Qs (Low to High)', 'New to Old', 'Old to New', 'A-Z', 'Z-A'];

  const fetchNotebooks = async (packId) => {
    try {
      setLoading(true);
      if (packId) {
        const data = await getNotebooks(packId);
        const normalizedNotebooks = Array.isArray(data) ? data.map(normalizeNotebook) : [];
        setNotebooks(normalizedNotebooks);
      } else {
        setNotebooks([]);
      }
    } catch (error) {
      setNotebooks([]);
    } finally {
      setLoading(false);
    }
  };

  const notebooksPackId = selectedPack?.id || localStorage.getItem('selectedPackId');

  useEffect(() => {
    fetchNotebooks(notebooksPackId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notebooksPackId]);

  const handleContextMenu = (event, notebook) => {
    event.preventDefault();
    setContextMenu({
      notebook,
      position: { x: event.clientX, y: event.clientY },
    });
  };

  const contextMenuItems = [
    {
      id: 'rename',
      label: 'Rename',
      icon: PencilSimpleIcon,
      action: 'rename',
      hoverBg: 'q3-surface-dimmer',
    },
    {
      id: 'delete',
      label: 'Delete',
      icon: TrashIcon,
      action: 'delete',
      hoverBg: 'q3-danger-light',
    },
  ];

  const handleContextMenuAction = (action, notebook) => {
    switch (action) {
      case 'rename':
        setCurrentNotebook(notebook);
        setNotebookName(notebook.name);
        setIsEditing(true);
        break;
      case 'delete':
        setCurrentNotebook(notebook);
        setShowDeleteModal(true);
        break;
      default:
        break;
    }
  };

  const handleUpdateNotebook = async () => {
    try {
      const data = await updateNotebook(currentNotebook.id, notebookName);
      if (data.success) {
        const updatedNotebook = {
          ...currentNotebook,
          name: notebookName,
          title: notebookName,
        };
        handleNotebookUpdate(updatedNotebook);
      }
    } catch (error) {
      setNotebookName(currentNotebook.name);
    } finally {
      setIsEditing(false);
    }
  };

  const handleArchiveNotebook = async () => {
    try {
      const data = await archiveNotebook(currentNotebook.id);
      if (data.success) {
        handleNotebookDelete(currentNotebook.id);
        window.webengage.track('Notebook Deleted', {
          'User Id': user?._id,
          'Notebook Name': currentNotebook.name,
          'Notebook Id': currentNotebook.id,
        });
      }
    } catch (error) {
    } finally {
      setShowDeleteModal(false);
    }
  };

  const handleNotebookUpdate = (updatedNotebook) => {
    setNotebooks((prevNotebooks) =>
      prevNotebooks.map((notebook) =>
        notebook.id === updatedNotebook.id ? { ...notebook, ...updatedNotebook } : notebook,
      ),
    );
  };

  const handleNotebookDelete = (notebookId) => {
    setNotebooks((prevNotebooks) => prevNotebooks.filter((notebook) => notebook.id !== notebookId));
  };

  // Update the onNotebookClick to pass the callback functions
  const handleNotebookClickWithCallbacks = (notebook) => {
    onNotebookClick(notebook, handleNotebookUpdate, handleNotebookDelete);
  };

  const filteredNotebooks = (Array.isArray(notebooks) ? notebooks : [])
    .filter((notebook) => notebook?.name?.toLowerCase().includes(searchTerm.toLowerCase()))
    .sort((a, b) => {
      switch (sortBy) {
        case 'Qs (High to Low)':
          return (b.questionCount || 0) - (a.questionCount || 0);
        case 'Qs (Low to High)':
          return (a.questionCount || 0) - (b.questionCount || 0);
        case 'New to Old':
          return new Date(b.createdAt) - new Date(a.createdAt);
        case 'Old to New':
          return new Date(a.createdAt) - new Date(b.createdAt);
        case 'A-Z':
          return a.name.localeCompare(b.name);
        case 'Z-A':
          return b.name.localeCompare(a.name);
        default:
          return 0;
      }
    });

  if (loading) {
    return <MyNotebooksLoader />;
  }

  return (
    <div className="w-full min-w-0 overflow-hidden flex flex-col h-full">
      {/* Edit Modal */}
      <EditModal
        isOpen={isEditing}
        onClose={() => {
          setIsEditing(false);
          if (currentNotebook) setNotebookName(currentNotebook.name);
        }}
        title="Notebook Name"
        value={notebookName}
        onChange={setNotebookName}
        onSave={handleUpdateNotebook}
      />

      {/* Delete Modal */}
      <DeleteModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleArchiveNotebook}
        title="Delete Notebook"
        description="This will delete all the questions present in the notebook, Are you sure you want to delete it?"
      />

      {/* Context Menu */}
      {contextMenu && (
        <ContextMenu
          items={contextMenuItems}
          position={contextMenu.position}
          onClose={() => setContextMenu(null)}
          onItemClick={(action) => handleContextMenuAction(action, contextMenu.notebook)}
        />
      )}

      {/* Sticky Header and Sort */}
      <div className="sticky top-0 z-30 bg-[var(--q3-surface-default)] px-4 pt-6 pb-4 md:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          {/* Mobile Title */}
          <div className="flex items-center gap-3 md:hidden mb-4">
            {onBack && (
              <button onClick={onBack} className="p-2">
                <ArrowLeftIcon className="w-5 h-5 text-[var(--q3-neutral-default)]" />
              </button>
            )}
            <div className="flex-1 text-center">
              <h1 className="text-xl font-bold text-[var(--q3-neutral-default)]">My Notebooks</h1>
              <p className="text-xs text-[var(--q3-neutral-light)]">Collection of all questions you have bookmarked</p>
            </div>
          </div>

          {/* Desktop Title */}
          <div className="hidden md:block flex-1">
            <h1 className="text-2xl font-bold text-[var(--q3-neutral-default)] mb-1">My Notebooks</h1>
            <p className="text-[var(--q3-neutral-light)]">Collection of all questions you have bookmarked</p>
          </div>

          {/* Search Bar */}
          <div className="relative w-full md:w-52 lg:w-60 mb-4 md:mb-0">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[var(--q3-neutral-light)] w-5 h-5 cursor-pointer" />
            <input
              type="text"
              placeholder="Search Notebook"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 rounded-full border border-[var(--q3-stroke-normal)] bg-[var(--q3-surface-default)] text-[var(--q3-neutral-default)] placeholder-[var(--q3-neutral-light)] focus:outline-none focus:ring-2 focus:ring-[var(--q3-accent-normal)] focus:border-transparent text-sm"
            />
          </div>
        </div>

        {/* Sort Buttons */}
        <div className="flex items-center gap-3 flex-wrap md:mt-4">
          <span className="text-sm text-[var(--q3-neutral-default)] flex-shrink-0">Sort by</span>
          <div className="flex items-center gap-2 overflow-x-auto pb-2 flex-nowrap">
            {sortOptions.map((option) => (
              <button
                key={option}
                onClick={() => setSortBy(option)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm whitespace-nowrap flex-shrink-0 transition-colors cursor-pointer
                ${
                  sortBy === option
                    ? 'bg-[var(--q3-accent-normal)] text-white'
                    : 'bg-[var(--q3-surface-default)] text-[var(--q3-neutral-default)] hover:bg-[var(--q3-surface-dimmest)] border border-[var(--q3-stroke-normal)]'
                }`}
              >
                <span>{option}</span>
                {sortBy === option && <CheckIcon size={14} weight="bold" />}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Scrollable Notebook Grid */}
      <div className="flex-1 overflow-y-auto pb-6 px-2 sm:px-4 md:px-6 lg:px-8">
        {filteredNotebooks.length > 0 ? (
          <div className="flex justify-center sm:justify-start">
            <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-5 gap-3 sm:gap-3 md:gap-6">
              {filteredNotebooks.map((notebook) => (
                <div
                  key={notebook.id || notebook._id}
                  className="relative group"
                  onContextMenu={(e) => handleContextMenu(e, notebook)}
                >
                  <NotebookCard
                    title={notebook.name}
                    count={notebook.questionCount || 0}
                    notebook={notebook}
                    onClick={() => handleNotebookClickWithCallbacks(notebook)}
                    className="hover:scale-105 transition-transform duration-200 mt-6"
                  />
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-16 text-center cursor-pointer">
            <MagnifyingGlassIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-[var(--q3-neutral-default)] mb-2">😕 No notebooks found</h3>
            <p className="text-[var(--q3-neutral-light)] mb-6">
              {searchTerm ? 'Try adjusting your search criteria' : 'Create your first notebook to get started'}
            </p>
            {searchTerm && (
              <button
                onClick={() => {
                  setSearchTerm('');
                  setActiveFilter('All Notebooks');
                  setSortBy('Qs (High to Low)');
                }}
                className="rounded-full bg-[var(--q3-accent-normal)] hover:bg-[var(--q3-accent-dark)] text-white px-6 py-2 transition-colors duration-300"
              >
                Clear Filters
              </button>
            )}
          </div>
        )}
      </div>

      {/* Edit Name Modal */}
      {isEditing && currentNotebook && (
        <div
          className="fixed inset-0 flex items-center justify-center z-50 p-4"
          style={{
            background: 'rgba(16, 24, 40, 0.70)',
            backdropFilter: 'blur(6px)',
            WebkitBackdropFilter: 'blur(6px)',
          }}
        >
          <div
            className="p-6 shadow-2xl w-[95%] sm:w-[440px] md:w-[460px] max-w-[95vw]"
            style={{
              background: 'var(--q3-surface-default)',
              borderRadius: '16px',
            }}
          >
            <h3 className="text-base font-semibold mb-3 text-[var(--q3-neutral-default)]">Notebook Name</h3>
            <input
              type="text"
              value={notebookName}
              onChange={(e) => setNotebookName(e.target.value)}
              className="w-full p-3 mb-5 text-sm rounded outline-none focus:border-[1px]"
              style={{
                border: '1px solid #D0D5DD',
                color: 'var(--q3-neutral-default)',
                borderRadius: '8px',
              }}
              onFocus={(e) => (e.target.style.border = '2px solid var(--q3-accent-normal)')}
              onBlur={(e) => (e.target.style.border = '1px solid #D0D5DD')}
              placeholder="Enter notebook name"
            />
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setIsEditing(false);
                  setNotebookName(currentNotebook.name);
                }}
                className="rounded-full border border-[var(--q3-stroke-normal)] px-5 py-2 text-[var(--q3-neutral-default)]"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  handleUpdateNotebook(currentNotebook);
                  setIsEditing(false);
                }}
                className="rounded-full bg-[var(--q3-accent-normal)] px-5 py-2 text-white"
              >
                Update Name
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
